import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import {
  ADD_OR_EDIT_ROUTE_REGEX,
  drawersAtom,
  isValidAppPath,
  localStorageHelpers,
  RoutePaths,
  usePrefixedRouting,
} from "@ryde/shared";

import { useSessionStore } from "../contexts/use-session-store";

export const useSimpleNavigation = () => {
  const { navigatePrefixed } = usePrefixedRouting();
  const location = useLocation();
  const setDrawers = useSetRecoilState(drawersAtom);
  const { rebootSession } = useSessionStore();

  const isAddOrEditRoute = useMemo(
    () => ADD_OR_EDIT_ROUTE_REGEX.test(location.pathname),
    [location.pathname],
  );

  const isEditRoute = useMemo(
    (): boolean => location.pathname.includes("/edit/"),
    [location.pathname],
  );

  const isAddRoute = useMemo(
    (): boolean => location.pathname.split("/").includes("add"),
    [location.pathname],
  );

  const isPreCreationRoute = useMemo(
    (): boolean => location.pathname.includes("/new"),
    [location.pathname],
  );

  const navigateHome = useEvent((): void => {
    setDrawers([]);
    navigatePrefixed(RoutePaths.HOME);
  });

  const navigateLogin = useEvent((): void => {
    localStorageHelpers.setItem("returnTo", null);
    navigatePrefixed(RoutePaths.LOGIN);
  });

  const navigateProfile = useEvent((): void => {
    navigatePrefixed(RoutePaths.PROFILE);
  });

  const navigatePageNotFound = useEvent((): void => {
    setDrawers([]);
    navigatePrefixed(RoutePaths.PAGE_NOT_FOUND);
  });

  // Called on login success only. Remounts RecoilRoot so no Recoil state from a previous
  // account's session can survive into the new one — login can follow an expired session without a logout,
  // so the reset must live here and not only in the logout flow (RYDE-8042).
  const navigateToHomeOrLoginReturnUrl = useEvent(() => {
    const returnUrl = localStorageHelpers.getItem<string>("returnTo");
    if (returnUrl && isValidAppPath(returnUrl)) {
      // Keep returnTo until setOrChooseProfile consumes it for company auto-select.
      window.location.hash = returnUrl;
    } else {
      if (returnUrl) {
        localStorageHelpers.setItem("returnTo", null);
      }
      window.location.hash = RoutePaths.HOME;
    }
    rebootSession();
  });

  return {
    isAddRoute,
    isEditRoute,
    isPreCreationRoute,
    navigateHome,
    navigateProfile,
    navigatePageNotFound,
    navigateLogin,
    isAddOrEditRoute,
    navigateToHomeOrLoginReturnUrl,
  };
};
